import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedAssessmentPeriodId,
  getLeadershipTeams,
  getCurrentOrganizationId,
  getMostRecentClosedAssessmentPeriodId,
} from 'common/selectors/auth';
import userContextSlice from 'common/slices/userContextSlice';
import { useGetAssessmentPeriodsByScopeQuery } from '@/areas/assessment-schedule/api/assessmentPeriodApi';
import {
  ASSESSMENT_PERIOD_CLOSED,
  ASSESSMENT_PERIOD_PLAN,
  ASSESSMENT_PERIOD_ACT,
  ASSESSMENT_PERIOD_CLOSED_NEW,
} from '@/areas/assessment-schedule/constants';

export function useCultureHubQueryParams() {
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const leadershipTeams = useSelector(getLeadershipTeams);
  const organizationId = useSelector(getCurrentOrganizationId);
  const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;
  const leadershipTeamsForSelectedAssessmentPeriod = useMemo(() => {
    if (!selectedAssessmentPeriodId || !Array.isArray(leadershipTeams)) {
      return leadershipTeams;
    }

    return leadershipTeams.filter(team =>
      Array.isArray(team.assessments) && team.assessments.includes(selectedAssessmentPeriodId)
    );
  }, [leadershipTeams, selectedAssessmentPeriodId]);

  const dispatch = useDispatch();
  const teamId =
    selectedTeamId != null &&
    leadershipTeamsForSelectedAssessmentPeriod?.some(team => team.id === selectedTeamId)
      ? selectedTeamId
      : leadershipTeamsForSelectedAssessmentPeriod?.[0]?.id;
  const mostRecentClosedAssessmentPeriodId = useSelector(getMostRecentClosedAssessmentPeriodId);

  useEffect(() => {
    if (!Array.isArray(leadershipTeamsForSelectedAssessmentPeriod)) {
      return;
    }

    if (selectedTeamId == null) {
      return;
    }

    const isSelectedTeamValid = leadershipTeamsForSelectedAssessmentPeriod.some(
      team => team.id === selectedTeamId
    );

    if (!isSelectedTeamValid) {
      dispatch(userContextSlice.actions.setSelectedTeamId(teamId));
    }
  }, [dispatch, leadershipTeamsForSelectedAssessmentPeriod, selectedTeamId, teamId]);

  const { data: assessmentPeriods, isLoading: isAssessmentPeriodsLoading, isFetching: isAssessmentPeriodsFetching } = useGetAssessmentPeriodsByScopeQuery(
    undefined,
    { skip: !organizationId }
  );

  const assessmentPeriod = useMemo(() => {
    if (!assessmentPeriods?.length) return null;
    return assessmentPeriods.find(p => p.id === selectedAssessmentPeriodId) ?? assessmentPeriods[0];
  }, [assessmentPeriods, selectedAssessmentPeriodId]);

  const shouldFetchData = useMemo(() => {
    const status = assessmentPeriod?.status;
    if (!status) return false;
    return [
      ASSESSMENT_PERIOD_CLOSED,
      ASSESSMENT_PERIOD_PLAN,
      ASSESSMENT_PERIOD_ACT,
      ASSESSMENT_PERIOD_CLOSED_NEW,
    ].includes(status);
  }, [assessmentPeriod?.status]);

  const isMostRecentClosedAssessmentPeriod = useMemo(
    () =>
      assessmentPeriod?.status === ASSESSMENT_PERIOD_CLOSED &&
      assessmentPeriod?.id === mostRecentClosedAssessmentPeriodId,
    [assessmentPeriod?.status, assessmentPeriod?.id, mostRecentClosedAssessmentPeriodId]
  );

  return {
    assessmentPeriodId: assessmentPeriod?.id ?? null,
    assessmentPeriodStatus: (assessmentPeriod?.status ?? null) as number | null,
    teamId,
    organizationId,
    shouldFetchData,
    isMostRecentClosedAssessmentPeriod,
    isAssessmentPeriodsLoading: isAssessmentPeriodsLoading || isAssessmentPeriodsFetching,
  };
}
